<template>
  <el-popover
    v-model="visible"
    trigger="click"
    placement="right-end"
    :popper-class="b('popper')"
    :visible-arrow="false"
    @show="onShow"
    @after-leave="onAfterHide"
  >
    <template #reference>
      <el-avatar
        :class="[b('ref'), { 'is-active': isActive }]"
        :size="32"
        :src="avatar"
      />
    </template>

    <div
      v-if="isActive"
      :class="b('menu')"
    >
      <el-row
        type="flex"
        align="middle"
        :class="b('info')"
      >
        <el-avatar
          :size="36"
          :src="avatar"
        />
        <div :class="$style.avatarRightBox">
          <div
            v-overflow-tooltip="userName"
            class="text-ellipsis"
          >
            {{ userName }}
          </div>
          <div :class="b('email')">
            {{ email || phone }}
          </div>
        </div>
      </el-row>

      <el-divider v-if="!isPrivateVersion" />
      <AIUsage
        v-if="!isPrivateVersion"
        :visible="visible"
        @hide="hide"
      />

      <template v-for="(group) of menus">
        <el-divider :key="group.key" />
        <template v-for="menu of group.children.filter(m => !m.hidden)">
          <el-popover
            v-if="menu.key === 'qrcode'"
            :key="menu.key"
            v-model="showQRCode"
            trigger="click"
            placement="right-end"
            @show="onQRCodeShow"
          >
            <transition name="el-fade-in-linear">
              <el-image
                :src="qrURL"
                :class="b('qr-code')"
              >
                <div slot="placeholder">
                  <em :class="['el-icon-loading']" />
                </div>
                <div slot="error">
                  <em :class="['el-icon-loading']" />
                </div>
              </el-image>
            </transition>
            <DropdownItem
              slot="reference"
              :icon="menu.icon"
              :title="menu.label"
              :active="showQRCode"
              @click="() => {}"
            />
          </el-popover>
          <DropdownItem
            v-else
            :key="menu.key"
            :icon="menu.icon"
            :title="menu.label"
            :blank="menu.blank"
            @click="menu.fn"
          />
        </template>
      </template>
    </div>
  </el-popover>
</template>

<script>
import { addUrlParams, resolveUrl } from '@zk/framework-vue';
import theme from '@zk/theme/lib/variables.scss';
import { mapActions, mapGetters } from 'vuex';
import DropdownItem from 'common-module/src/components/DropdownItem';
import { clearAndSamlLogout } from 'common-module/src/util/http';
import { getQrCode } from '@/api/home';
import { getSamlConfig, samlLogoutPrepare } from '@/api/saml';
import config from 'common-module/src/config';
import AIUsage from './AIUsage.vue';
import {
  customAlias,
  integration,
  mgrApi,
  orgInfo,
  recycleBin,
  systemFieldMgr,
  systemImport,
  systemSetting,
  upgrade,
  settingOrganizationalMgr,
  executeForSome,
  settingMember,
  settingMemberGroup,
  settingPermissionReadonlyOrMgr,
  settingSubscription,
  settingBilling
} from 'common-module/src/config/permission';
import { isOverseasVersion, isPrivateVersion } from 'common-module/src/util';

export default {
  name: 'AvatarMenu',
  components: {
    DropdownItem,
    AIUsage
  },
  bemNS: theme.bemNS,
  data() {
    return {
      visible: false,
      isActive: false,
      qrURL: null,
      showQRCode: false,
      upgradePermission: upgrade
    };
  },
  computed: {
    ...mapGetters('user', ['avatar', 'userName', 'email', 'phone', 'getAICredit', 'checkAccessPermission']),
    menus() {
      return [{
        key: '1-1',
        children: [{
          key: 'settings',
          label: this.$t('setting.myprofile'),
          icon: 'zkicon iconpeizhi',
          blank: true,
          hidden: !this.checkAccessPermission(systemSetting),
          fn: this.toSettings
        }, {
          key: 'integration',
          label: this.$t('navbar.integration'),
          icon: 'zkicon iconjicheng',
          blank: true,
          hidden: !this.checkAccessPermission(integration),
          fn: this.toIntegration
        }, {
          key: 'userCenter',
          label: this.$t('navbar.userCentre'),
          icon: 'zkicon icongerenzhongxin',
          blank: true,
          fn: this.openUserCenter
        }, {
          key: 'upgrade',
          label: this.$t('navbar.userRights.upgrade'),
          icon: 'zkicon iconhuojian',
          blank: true,
          hidden: !this.checkAccessPermission(upgrade) || this.isPrivateVersion || this.$store.getters.isAWSUserMark,
          fn: this.openUpgrade
        }, {
          key: 'organizational',
          label: this.$t('navbar.organizational'),
          icon: 'zkicon iconzuzhiguanli',
          blank: true,
          hidden: !this.checkAccessPermission(settingOrganizationalMgr),
          fn: this.openOrg
        }, {
          key: 'customMenu',
          label: this.$t('setting.menu.title'),
          icon: 'zkicon iconzidingyi',
          blank: true,
          fn: this.openCustomMenu
        }, {
          key: 'recycleBin',
          label: this.$t('issue.recycleBin'),
          icon: 'zkicon icondelete',
          blank: true,
          hidden: !this.checkAccessPermission(recycleBin),
          fn: this.openRecycleBin
        }]
      }, {
        key: '1-2',
        children: [{
          key: 'website',
          label: this.$t('navbar.visitWebsites'),
          icon: 'zkicon iconfangwenguanwang',
          blank: true,
          hidden: this.isPrivateVersion,
          fn: this.openWebsite
        }, {
          key: 'qrcode',
          label: this.$t('navbar.miniProgram'),
          icon: 'zkicon iconxiaochengxu',
          hidden: isOverseasVersion ? true : this.isPrivateVersion
        }, {
          key: 'logout',
          label: this.$t('sso.login.exit_login'),
          icon: 'zkicon icontuichudenglu',
          fn: this.logoutHandler
        }]
      }];
    }
  },
  mounted() {
    window.addEventListener('message', this.onWindowMessage);
  },
  unmounted() {
    window.removeEventListener('message', this.onWindowMessage);
  },
  methods: {
    ...mapActions('user', ['logout']),
    init() {
      const onPopoverCreated = (popper) => {
        const { modifiers } = popper.popperJS._options;
        modifiers.splice(modifiers.length - 1, 0, (data) => {
          const pos = this.getPopoverPosition();
          if (pos) {
            data.offsets.popper.left = pos.x + pos.width - 1;
            data.offsets.popper.top = pos.y - 12;
          }
          return data;
        });
      };
      const { popover } = this.$refs;
      popover.$refs.popper.$on('created', onPopoverCreated);
      popover.$once('hook:beforeDestroy', () => popover.$refs.popper?.$off('created', onPopoverCreated));
    },
    onShow() {
      this.isActive = true;
      this.$track.send('PC_PersonalCenter', {}, true);
    },
    onAfterShow() {
      this.$refs.popover.updatePopper();
    },
    onAfterHide() {
      this.isActive = false;
    },
    async onQRCodeShow() {
      this.$track.send('PC_MiniProgram', {}, true);
      if (!this.qrURL) {
        const res = await getQrCode();
        this.qrURL = res?.url;
      }
    },
    hide() {
      this.visible = false;
    },
    toSettings() {
      const permissionOpenConfig = [{
        meta: systemFieldMgr,
        handler: () => {
          window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/fields`));
        }
      }, {
        meta: customAlias,
        handler: () => {
          window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/personalization`));
        }
      }, {
        meta: mgrApi,
        handler: () => {
          window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/api`));
        }
      }, {
        meta: systemImport,
        handler: () => {
          window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/import`));
        }
      }, {
        meta: integration,
        handler: () => {
          window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/integration`));
        }
      }];
      this.$track.send('PC_setting', {}, true);
      this.hide();
      executeForSome(permissionOpenConfig, this.checkAccessPermission);
    },
    toIntegration() {
      window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/integration`));
      this.hide();
    },
    openUserCenter() {
      window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/account`));
      this.hide();
    },
    openOrg() {
      executeForSome([{
        meta: orgInfo,
        handler: () => window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/orginfo`))
      }, {
        meta: settingMember,
        handler: () => window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/members`))
      }, {
        meta: settingMemberGroup,
        handler: () => window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/members/group-manage`))
      }, {
        meta: settingPermissionReadonlyOrMgr,
        handler: () => window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/permission`))
      }, {
        meta: settingSubscription,
        handler: () => window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/subscription`))
      }, {
        meta: settingBilling,
        handler: () => window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/billing`))
      }], this.checkAccessPermission);
      this.hide();
    },
    openCustomMenu(){
      window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/menu`));
      this.hide();
    },
    openRecycleBin() {
      window.open(resolveUrl(`${this.$config.VUE_APP_PMS_URL}/setting/recycle-bin`));
      this.hide();
    },
    openWebsite() {
      this.$track.send('PC_website', { from: '点击访问官网' }, true);
      window.open(this.$config.VUE_APP_WEBSITE);
      this.hide();
    },
    openUpgrade() {
      this.eventBus.$emit('global:openUpgradeDialog');
      this.hide();
      /* eslint-disable */
                       
                                                                     
               
      /* eslint-disable */
    },
    async logoutHandler() {
      this.$track.send('PC_LogOut', {}, true);
      this.hide();
      if (isPrivateVersion) {
        try {
          // 私有化部署：检查当前组织是否启用了SAML且配置了logout地址
          const samlConfig = await getSamlConfig();
          if (samlConfig && samlConfig.idpName && samlConfig.logoutUrl) {
            // SAML用户：调后端prepare接口（失效token + 返回state）
            const state = await samlLogoutPrepare(samlConfig.idpName);
            if (state) {
              // 清理本地登录态后，以顶层浏览器导航完成 IdP SLO。
              await clearAndSamlLogout(resolveUrl(`${config.VUE_APP_PMS_URL}/api/saml/${samlConfig.idpName}/logout?state=${state}`));
              return;
            }
            // state获取失败，走正常退出流程
          }
        } catch (_) {
          // 获取SAML配置失败或prepare接口失败，走正常退出流程
        }
      }
      // 非私有化 / 非SAML用户 / 未配置logoutUrl / 构建失败：走正常退出
      try {
        await this.logout();
      } catch (_) {}
      document.write('');
      const url = new URL(window.location.href);
      url.searchParams.delete('pid');
      window.location.href = addUrlParams(resolveUrl(`${config.VUE_APP_SSO_URL}/login`), {
        'sso-redirect': url.href
      });

    },
    onWindowMessage(e) {
      if (e.data === 'need-refresh-user-info') {
        this.$store.dispatch('user/loadUserInfo');
      }
    }
  }
};
</script>

<style lang="scss">
@include b(avatar-menu) {
  @include e(ref) {
    cursor: pointer;
    background: transparent;
    position: relative;
    display: inline-block;
    // fix safari border radius overflow issue
    z-index: 0;
    // &:hover {
    //   border: 1px solid var(--primary-color);
    // }
    img{
      position: relative;
      z-index: 1;
    }
    &:after{
      content: '';
      position:absolute;
      left: 1px;
      right: 1px;
      bottom: 1px;
      top: 1px;
      background: #C0C4CC;
      z-index: 0;
      border-radius: 50%;
    }
    // @include when(active) {
    //   border: 1px solid var(--primary-color);
    // }
  }

  @include e(menu) {
    .el-divider {
      margin: var(--m-default);
      width: calc(100% - 32px);
    }
    .el-avatar {
      margin-right: 12px;
    }
  }
  @include e(popper) {
    @include when(popper, el-) {
      padding: 24px 8px 8px 8px;
      min-width: 260px;
      max-width: 300px;

      @media (max-width: 1290px) {
        max-height: 540px;
        overflow-y: auto;
      }
    }
  }
  @include e(info) {
    padding: 0 var(--m-default);
    color: var(--font-color-1);
  }
  @include e(email) {
    color: var(--font-color-2);
  }
  @include e(qr-code) {
    width: 160px;
    height: 160px;
    background-color: var(--main-bg-color);
    display: flex !important;
    align-items: center;
    justify-content: center;
  }
}
</style>
<style lang="scss" module>
.avatarRightBox {
  overflow: hidden;
}
</style>

